import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ImpersonationApiService } from '../../services/impersonation-api.service';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonationTunnel } from '../../models/impersonation.models';
import { CompanyContextService } from '../../../services/company-context.service';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../../shared/components/pagination-bar/pagination-bar.component';
import {
  handleImpersonationApiResult,
  notifyImpersonationFailure,
} from '../../utils/impersonation-ui.util';
import { ImpersonationConfirmModalComponent } from '../../../../shared/components/impersonation-confirm-modal';
import { ImpersonationEmptyStateComponent } from '../../../../shared/components/impersonation-empty-state';

/**
 * Tunnel impersonation for the company open in the detail shell. The company comes
 * from the route via CompanyContextService — this page no longer picks one.
 */
@Component({
  selector: 'app-impersonation-tunnels',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationBarComponent,
    ImpersonationConfirmModalComponent,
    ImpersonationEmptyStateComponent,
  ],
  templateUrl: './impersonation-tunnels.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./impersonation-tunnels.component.scss'],
})
export class ImpersonationTunnelsComponent implements OnInit, OnDestroy {
  private readonly api = inject(ImpersonationApiService);
  private readonly context = inject(CompanyContextService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly messageService = inject(NotificationService);

  companyId = '';
  tunnels: ImpersonationTunnel[] = [];
  loadingTunnels = false;
  errorMessage = '';
  busyKey: string | null = null;

  confirmVisible = false;
  confirmTunnel: ImpersonationTunnel | null = null;
  confirmLocationId = '';

  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 25;
  totalItems = 0;
  totalPages = 0;

  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;
  private contextSub?: Subscription;
  private tunnelsRequestSub?: Subscription;

  ngOnInit(): void {
    this.contextSub = this.context.current$.subscribe(company => {
      const id = company?.id || '';
      if (id === this.companyId) {
        return;
      }
      this.companyId = id;
      this.resetFilters();
      this.closeConfirm();
      this.loadTunnels();
    });

    this.searchSub = this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.loadTunnels();
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.contextSub?.unsubscribe();
    this.tunnelsRequestSub?.unsubscribe();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  private resetFilters(): void {
    this.tunnels = [];
    this.errorMessage = '';
    this.currentPage = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.searchTerm = '';
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.search$.next(value.trim());
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTunnels();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadTunnels();
  }

  loadTunnels(): void {
    this.tunnelsRequestSub?.unsubscribe();
    if (!this.companyId) {
      this.tunnels = [];
      this.loadingTunnels = false;
      return;
    }

    this.loadingTunnels = true;
    this.errorMessage = '';

    const params: {
      page: number;
      limit: number;
      search?: string;
    } = {
      page: this.currentPage,
      limit: this.itemsPerPage,
    };
    if (this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    this.tunnelsRequestSub = this.api.getShopTunnels(this.companyId, params).subscribe({
      next: res => {
        this.loadingTunnels = false;
        if (res.status && res.data) {
          this.tunnels = res.data.tunnels || [];
          this.totalItems = res.data.totalItems || 0;
          this.currentPage = res.data.currentPage || this.currentPage;
          this.totalPages = res.data.totalPages || 0;
          this.itemsPerPage = res.data.itemsPerPage || this.itemsPerPage;
          return;
        }
        this.tunnels = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = res.message || 'Unable to load tunnels.';
      },
      error: err => {
        this.loadingTunnels = false;
        this.tunnels = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load tunnels.';
      },
    });
  }

  companyName(): string {
    return this.context.name;
  }

  impersonate(tunnel: ImpersonationTunnel): void {
    if (!this.companyId) {
      return;
    }
    const locationId = (tunnel.location_id || '').trim();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot impersonate',
        detail: 'This tunnel has no location id.',
        life: 5000,
      });
      return;
    }

    this.confirmTunnel = tunnel;
    this.confirmLocationId = locationId;
    this.confirmVisible = true;
  }

  closeConfirm(): void {
    if (this.busyKey) {
      return;
    }
    this.confirmVisible = false;
    this.confirmTunnel = null;
    this.confirmLocationId = '';
  }

  onConfirmImpersonate(): void {
    if (!this.confirmTunnel || !this.confirmLocationId || !this.companyId || this.busyKey) {
      return;
    }
    this.startTunnelImpersonation(this.confirmTunnel, this.confirmLocationId);
  }

  private startTunnelImpersonation(tunnel: ImpersonationTunnel, locationId: string): void {
    this.busyKey = tunnel.id;
    this.api
      .impersonateTunnel({
        location_id: locationId,
        company_id: this.companyId || undefined,
        reason: 'support',
      })
      .subscribe({
        next: res => {
          this.busyKey = null;
          handleImpersonationApiResult(
            res,
            data => {
              const started = this.impersonation.startFromTunnelImpersonateApi(data, {
                company_id: this.companyId,
                company_name: this.context.name,
                timezone: this.context.timezone,
                location_id: locationId,
                branch: tunnel.branch || tunnel.location_name,
                name: tunnel.name || tunnel.location_name || tunnel.branch || 'Tunnel',
                email: tunnel.email,
              });
              this.closeConfirmAfterStart();
              return started;
            },
            this.messageService,
            'No token returned from impersonate/tunnel.'
          );
        },
        error: err => {
          this.busyKey = null;
          notifyImpersonationFailure(
            this.messageService,
            err?.error?.message || err?.message || 'Request failed.'
          );
        },
      });
  }

  private closeConfirmAfterStart(): void {
    this.confirmVisible = false;
    this.confirmTunnel = null;
    this.confirmLocationId = '';
  }
}
